import clsx from 'clsx'
import {
  checkIncompleteField,
  updateCheckboxConditionalLogic,
  updateEditOnPage,
  useDataTemplate,
} from 'components/form_builder/features/data/dataSlice'
import { metadataByPath } from 'lib/utilities/form'
import { isEmpty, isPlainObject } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Input from '../../Input'
import SectionDivider from '../SectionDivider'
import ConditionalLogic from './ConditionalLogic'
import SelectField from './SelectField'
import { SectionItem, FillOnlyPaths } from './Paths'

const OPTIONS = {
  edit_on_page: { id: 'edit_on_page', name: 'on page' },
  in_dropdown: { id: 'in_dropdown', name: 'in dropdown' },
}

const useFieldDefOptions = (path) => {
  const metadata = useSelector((state) => state.base.metadata)
  const fieldDefs = useSelector((state) => state.fields.field_defs)
  if (!path) return []

  if (fieldDefs?.[path]?.options) return fieldDefs[path].options

  if (fieldDefs?.[path]?.type === 'boolean') return ['yes', 'no']

  const field = metadataByPath(metadata, path)
  if (!field) return []

  if (!field.options && field.type === 'boolean') return ['yes', 'no']

  return field.options || []
}

const EditOnPage = ({ component }) => {
  const dispatch = useDispatch()
  const template = useDataTemplate(component.type, component.id)
  const isIncompleteField = checkIncompleteField(component.type, template)

  const [path, setPath] = useState('')
  const [valueOption, setValueOption] = useState(null)
  const valueOptions = useFieldDefOptions(path)

  useEffect(() => {
    setPath(template?.edit_on_page?.path)
    setValueOption(template?.edit_on_page?.value)
  }, [template])

  const storeValue = () => {
    const data = { path, value: valueOption, type: 'checkbox' }
    dispatch(updateEditOnPage({ id: component.id, type: component.type, editOnPage: data }))
    dispatch(updateCheckboxConditionalLogic({ id: component.id, type: component.type, conditionalLogic: {} }))
  }

  const handleOnchangePath = (e) => {
    setValueOption(null)
    setPath(e.target.value)
  }

  const handleSelectValueForState = (value) => {
    setValueOption(value.id)
    const data = { path, value: value.id, type: 'checkbox' }
    dispatch(updateEditOnPage({ id: component.id, type: component.type, editOnPage: data }))
  }

  return (
    <div className="dome-d-flex dome-flex-column dome-gap12">
      <div>
        <Input
          label="path for display:*"
          placeholder="type or paste path"
          value={path}
          required={isIncompleteField && !path}
          onBlur={storeValue}
          onChange={handleOnchangePath}
        />
        <SectionItem path={path} />
      </div>
      <SelectField
        label="choose value for checked state (true)"
        options={valueOptions}
        selectedOption={valueOption}
        required={isIncompleteField}
        onSelect={handleSelectValueForState}
      />
    </div>
  )
}

const InDropdown = ({ component }) => {
  const dispatch = useDispatch()
  const template = useDataTemplate(component.type, component.id)
  const isIncompleteField = checkIncompleteField(component.type, template)

  const [conditionLogic, setConditionLogic] = useState('{}')

  useEffect(() => {
    setConditionLogic(JSON.stringify(template?.checkbox?.conditional_logic, null, 4))
  }, [template])

  const handleSaveConditionalLogic = useCallback(
    (data) => {
      dispatch(updateEditOnPage({ id: component.id, type: component.type, editOnPage: {} }))
      dispatch(updateCheckboxConditionalLogic({ id: component.id, type: component.type, conditionalLogic: data || {} }))
    },
    [component]
  )

  return (
    <ConditionalLogic
      required={isIncompleteField}
      value={conditionLogic || '{}'}
      onSave={handleSaveConditionalLogic}
      onChange={(e) => setConditionLogic(e.target.value)}
      linkedPaths={template?.templatePaths || []}
    />
  )
}

export default function Checkbox({ id, component }) {
  const template = useDataTemplate(component.type, component.id)
  const [activeTab, setActiveTab] = useState(OPTIONS.edit_on_page.id)

  useEffect(() => {
    const editOnPage = template?.edit_on_page
    if (isPlainObject(editOnPage) && !isEmpty(editOnPage)) {
      setActiveTab(OPTIONS.edit_on_page.id)
    } else {
      setActiveTab(OPTIONS.in_dropdown.id)
    }
  }, [template])

  return (
    <>
      <SectionDivider sectionName="input method">
        <div className="dome-d-flex dome-flex-column dome-gap12">
          <div className="dome-d-flex dome-flex-column">
            <span className="dome-p3 dome-color-med-grey">select a method to input this field’s value(s):</span>
            <div
              style={{
                gap: '6px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              }}
            >
              {Object.values(OPTIONS).map(({ id, name }) => {
                const active = id === activeTab
                const className = clsx(
                  'dome-btn dome-btn-base',
                  active ? 'dome-btn-dark-green' : 'dome-btn-grey-stroke'
                )
                return (
                  <button key={id} type="button" onClick={() => setActiveTab(id)} className={className}>
                    {name}
                  </button>
                )
              })}
            </div>
          </div>
          {OPTIONS.edit_on_page.id === activeTab && <EditOnPage component={component} />}
          {OPTIONS.in_dropdown.id === activeTab && <InDropdown component={component} />}
        </div>
      </SectionDivider>
      {OPTIONS.in_dropdown.id === activeTab && <FillOnlyPaths id={id} component={component} />}
    </>
  )
}
